// React and Hooks
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// Third-party Libraries
import dayjs from 'dayjs'

// Material-UI Components
import Typography from '@mui/material/Typography'
import { Box, Button, LinearProgress } from '@mui/material'

// Constants and API
import { PAGE_TITLE } from '../../constants/constants'
import { API_URL } from '../../api/constants'

// Custom Hooks
import { useAxiosPrivate, useDocumentTitle } from '../../hooks'

// Layouts and Loaders
import Layout from '../../layouts/common/Layout'

// Common Components
import {
  CustomNoRowsOverlay,
  DefaultToolbarContainer,
} from '../../components/common/kc-table/components'
import { KcTable } from '../../components/common/kc-table'
import Modal from '../../components/common/Modal'

// HL7 Specific Components
import GlobalFilterForm from '../../components/common/GlobalFilterForm'
import HL7EventDetail from '../../components/HL7EventDetail/HL7EventDetail'

// Redux Slices
import {
  fetchFacilities,
  getFetchLoading,
  getFetchError,
  getFacilities,
} from '../slices/facility.slice'
import {
  fetchHL7Logs,
  getHL7Logs,
  getHL7LogsError,
  getHL7LogsLoading,
  hl7LogActions,
} from '../slices/hl7Log.slice'

// Utilities
import { formatHL7Logs } from './utils'
import { getColumns } from './columns'
import { parseAsInteger, useQueryState } from 'nuqs'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'

/**
 * HL7historyLogs Component
 *
 * This component is responsible for rendering the HL7 history logs page. It includes
 * functionality for fetching and displaying HL7 logs, filtering logs, and viewing
 * detailed information about a specific log entry.
 *
 * Features:
 * - Fetches HL7 logs and facilities data from the server.
 * - Supports server-side pagination and sorting.
 * - Allows filtering logs by various criteria such as date range and custom filters.
 * - Displays logs in a table with customizable columns and sorting options.
 * - Provides a modal to view detailed information about a specific HL7 log entry.
 *
 * Hooks:
 * - `useDocumentTitle`: Sets the document title for the page.
 * - `useQueryState`: Manages query parameters for pagination and filtering.
 * - `useSelector`: Retrieves Redux state for logs, facilities, errors, and loading status.
 * - `useDispatch`: Dispatches Redux actions for fetching logs and facilities.
 * - `useEffect`: Triggers data fetching on component mount and query parameter changes.
 *
 * Props:
 * - None
 *
 * State Management:
 * - `pageNo` (number): Current page number for pagination.
 * - `pageSize` (number): Number of rows per page for pagination.
 * - `id` (string): Selected log ID for viewing details in the modal.
 *
 * Dependencies:
 * - Redux actions: `fetchHL7Logs`, `fetchFacilities`, `hl7LogActions.setSelectedLogId`.
 * - Utility functions: `formatHL7Logs`, `getColumns`.
 * - Components: `Modal`, `HL7EventDetail`, `HL7historyLogsGlobalFilterForm`, `KcTable`.
 *
 * Error Handling:
 * - Displays an error message if the server returns an error (except for status 421).
 *
 * Returns:
 * - JSX.Element: The rendered HL7 history logs page.
 */
const HL7historyLogs = () => {
  useDocumentTitle(PAGE_TITLE.HL7HISTORY_LOGS)

  const [pageNo, setPage] = useQueryState(
    'pageNo',
    parseAsInteger.withDefault(0)
  )
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(50)
  )
  const [id, setId] = useQueryState('id', { type: 'string' })

  const modalRef = useRef(null)
  const axiosPrivate = useAxiosPrivate()
  const dispatch = useDispatch()

  const hl7historyLogsData = useSelector(getHL7Logs)
  const error = useSelector(getHL7LogsError)
  const loading = useSelector(getHL7LogsLoading)
  const fetchLoading = useSelector(getFetchLoading)
  const fetchError = useSelector(getFetchError)
  const facilitiesData = useSelector(getFacilities)

  const queryOptions = useMemo(() => ({ pageNo, pageSize }), [pageNo, pageSize])

  const formattedHL7logs = useMemo(() => {
    if (fetchError) {
      console.error('Error fetching facilities:', fetchError)
      return []
    }
    if (fetchLoading) {
      return []
    }
    return formatHL7Logs(hl7historyLogsData?.listOfLogs, facilitiesData || [])
  }, [fetchError, fetchLoading, hl7historyLogsData, facilitiesData])

  const buildQueryString = useCallback(
    (options) => {
      const queryParams = {
        pageNo,
        pageSize,
        ...(options?.filter && options?.value
          ? { [options.filter]: options.value }
          : {}),
        ...(options?.startDate && options?.endDate
          ? {
              from_date: dayjs(options.startDate).format('YYYY-MM-DD'),
              till_date: dayjs(options.endDate).format('YYYY-MM-DD'),
            }
          : {}),
      }

      return Object.entries(queryParams)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')
    },
    [pageNo, pageSize]
  )

  const fetchHL7historyLogsData = useCallback(
    (options = {}) => {
      const queryString = buildQueryString(options)
      dispatch(
        fetchHL7Logs({
          axiosInstance: axiosPrivate,
          path: `${API_URL.HL7_HISTORY_LOGS}?${queryString}`,
        })
      )
    },
    [dispatch, axiosPrivate, buildQueryString]
  )

  useEffect(() => {
    dispatch(fetchFacilities(axiosPrivate))
  }, [dispatch, axiosPrivate])

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    fetchHL7historyLogsData({
      ...queryOptions,
      filter: queryParams.get('filter'),
      value: queryParams.get('value'),
      startDate: queryParams.get('startDate'),
      endDate: queryParams.get('endDate'),
    })
  }, [queryOptions, fetchHL7historyLogsData])

  const columns = getColumns({
    onView: (id) => () => {
      setId(id)
      dispatch(hl7LogActions.setSelectedLogId(id))
      modalRef?.current?.open()
    },
  })

  const initialState = {
    sorting: {
      sortModel: [{ field: 'createdAt', sort: 'desc' }],
    },
  }

  const renderError = () => {
    if (fetchError) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            Error fetching facilities. Please try again later.
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    console.log('Error fetching HL7 logs:', error)
    if (error?.status === 401) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            Unauthorized access. Please log in again.
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    return (
      <Layout.CenterWrapper>
        <Typography variant="h6" color="error">
          An error occurred while fetching HL7 logs. Please try again later.
        </Typography>
      </Layout.CenterWrapper>
    )
  }

  if (error && error?.status !== 421) {
    return renderError()
  }

  const hl7historyLogIds = formattedHL7logs?.map((log) => log.id) || []
  const currentLogIndex = hl7historyLogIds.indexOf(id)

  return (
    <div style={{ height: 'auto' }}>
      <Modal
        onClose={() => {
          setId(null)
          dispatch(hl7LogActions.clearHL7EventDetails())
          modalRef?.current?.close()
        }}
        ref={modalRef}
      >
        <Box display="flex" alignItems="center" gap={2} ml="auto" sx={{
          position: 'absolute',
          top: '.75rem',
          right: '4rem'
        }}>
          <Button
            variant='outlined'
            onClick={() => setId(hl7historyLogIds[currentLogIndex - 1])}
            disabled={currentLogIndex <= 0}
            title="Previous log"
            startIcon={<ArrowBackIos />}
          >
            Previous
          </Button>
          <Button
            variant='outlined'
            onClick={() => setId(hl7historyLogIds[currentLogIndex + 1])}
            disabled={currentLogIndex >= hl7historyLogIds.length - 1}
            title="Next log"
            endIcon={<ArrowForwardIos />}
          >
            Next
          </Button>
        </Box>
        <HL7EventDetail logId={id} />
      </Modal>
      {/* HL7 Logs Filter Form (Reusable) */}
      <GlobalFilterForm
        // showDateFilter={false}
        filters={[
          {
            title: 'KardiaPro Team',
            value: 'kpro_team_id',
            valueInputType: 'autocomplete',
            valueOptions: facilitiesData || [],
            valueOptionLabel: (facility) => facility?.facility_name,
            valueOptionValue: (facility) => facility?.kpro_team_id,
          },
          {
            title: 'Order Number',
            value: 'order_number',
            valueInputType: 'text',
          },
        ]}
        loading={fetchLoading}
        error={fetchError}
        fetchButtonLabel="Fetch Logs"
        resetButtonLabel="Reset"
        onFetch={fetchHL7historyLogsData}
        onReset={() => fetchHL7historyLogsData({})}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 200,
        }}
      >
        <KcTable
          rows={formattedHL7logs || []}
          autoHeight
          maxHeight="100%"
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={hl7historyLogsData?.totalCount || 0}
          page={pageNo}
          pageSize={pageSize}
          paginationMode="server"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pagination
          loading={loading}
          sortingOrder={['desc', 'asc']}
          initialState={initialState}
          rowsPerPageOptions={[5, 10, 25, 50]}
          components={{
            LoadingOverlay: LinearProgress,
            Toolbar: DefaultToolbarContainer,
            NoResultsOverlay: () => (
              <CustomNoRowsOverlay text="No Logs Available" />
            ),
            NoRowsOverlay: () => <CustomNoRowsOverlay text="No Logs Found" />,
          }}
        />
      </div>
    </div>
  )
}

export default HL7historyLogs
